import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import {
  CurrentSubscriptionService,
  SubscriptionFeatureService,
  SubscriptionProductService,
} from '@rma/billing/subscription-plans/public/api-subscription-features';
import { SubscribeIntentionService } from '@rma/billing/subscription-plans/public/api-subscription-intent';
import { SubscribeIntentionEnum } from '@rma/billing-api';
import { SubscriptionTiers } from '@rma/generic/domain/to-be-sorted';
import { RouteUtilityService } from '@rma/generic/util/router-utils';
import { combineLatest, debounceTime, map, Observable, take } from 'rxjs';

@Component({
  selector: 'rma-nav-subscription-header',
  templateUrl: './nav-subscription-header.component.html',
  host: {
    class: 'flex p-4 text-sm grow justify-end',
  },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, TranslocoModule, MatButtonModule, RouterModule],
})
export class NavSubscriptionHeaderComponent {
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly subscriptionFeatureService = inject(SubscriptionFeatureService);
  private readonly router = inject(RouteUtilityService);
  private readonly subscribeIntentionService = inject(SubscribeIntentionService);
  private readonly subscriptionProductService = inject(SubscriptionProductService);

  protected readonly showFreeTrial$: Observable<boolean>;
  protected readonly showUpgradeButton$: Observable<boolean>;
  protected readonly trialPeriodDays$: Observable<number>;

  public constructor() {
    const canUpgrade$ = this.subscriptionFeatureService.isPremium$.pipe(map((x) => !x));

    const hideUpgradeButton$ = this.router.endEvents.pipe(map((routeChange) => routeChange.data?.['hideUpgradeButton'] ?? false));

    this.trialPeriodDays$ = this.subscriptionProductService.availableTrialDays$;

    this.showFreeTrial$ = combineLatest([
      this.subscriptionProductService.availableTrialDays$,
      this.currentSubscriptionService.currentSubscriptionSummary$,
    ]).pipe(
      map(
        ([freeTrialDays, sub]) =>
          freeTrialDays > 0 && (sub?.subscriptionTier === SubscriptionTiers.Free || sub?.subscriptionTier === SubscriptionTiers.MLSFree),
      ),
    );

    this.showUpgradeButton$ = combineLatest([canUpgrade$, hideUpgradeButton$]).pipe(
      map(([canUpgrade, hideUpgradeButton]) => canUpgrade && !hideUpgradeButton),
    );
  }

  public onClick(): void {
    this.subscribeIntentionService.recordUserSubscribeIntention(SubscribeIntentionEnum.Header).pipe(take(1), debounceTime(500)).subscribe();
  }
}
